import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Icon,
  IconButton,
  Paper,
  Stack,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
  useTheme,
} from "@mui/material";
import { MobileDatePicker } from "@mui/x-date-pickers";
import { addDays, format, isToday, subYears } from "date-fns";
import { useEffect, useMemo, useRef, useState } from "react";
import {
  CartesianGrid,
  ComposedChart,
  Legend,
  Line,
  ReferenceDot,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import {
  ForecastSlot,
  PriceForecastResponse,
  PricesData,
  useGetPriceForecast,
  useGetPrices,
  useGetPricesAvailability,
  useRefreshPrices,
} from "api";
import { StickyTable } from "components/elements/mui";
import { CurrentPricesWidget } from "components/elements/widgets";
import {
  formatTimestamp,
  getCurrentDate,
  getCurrentSlot,
  slotPeriod,
} from "components/elements/widgets/Prices/utils";
import { PageLayout } from "components/layouts";
import { useFormatMessage, useLocaleContext } from "lang";
import { getSelectedPowerPlant, useAppSelector } from "store";
import { pulseSx, sourceFlashDurationMs } from "styles/animations";
import { priceSeriesColors } from "styles/theme";
import { useChartNavigation } from "utils/hooks/useChartNavigation";
import { useFlashOnce } from "utils/hooks/useFlashOnce";
import { useNow } from "utils/hooks/useNow";

type Row = {
  time: string;
  pse: number | null;
  pseActual: number | null;
  rdn: number | null;
  // PSE nowcast for the slot, when one covers this day.
  cenForecast: ForecastSlot | null;
  cenP50: number | null;
};

// Recharts' stock legend lays its items out inline and can run off the
// side of a narrow phone once all four series are on screen. This one wraps
// onto a second row instead, keeping each series' swatch (solid or dashed).
const WrappingLegend = ({
  payload,
}: {
  payload?: {
    value: string;
    color?: string;
    payload?: { strokeDasharray?: string | number };
  }[];
}) => (
  <Box
    component="ul"
    sx={{
      listStyle: "none",
      m: 0,
      p: 0,
      pt: 0.5,
      display: "flex",
      flexWrap: "wrap",
      justifyContent: "center",
      columnGap: 1.5,
      rowGap: 0.25,
      fontSize: 12,
      color: "text.secondary",
    }}
  >
    {(payload ?? []).map((item) => (
      <Box
        component="li"
        key={item.value}
        sx={{ display: "flex", alignItems: "center", gap: 0.5, whiteSpace: "nowrap" }}
      >
        <svg width={22} height={6} aria-hidden>
          <line
            x1={0}
            y1={3}
            x2={22}
            y2={3}
            stroke={item.color}
            strokeWidth={2}
            strokeDasharray={item.payload?.strokeDasharray}
          />
        </svg>
        <span style={{ color: item.color }}>{item.value}</span>
      </Box>
    ))}
  </Box>
);

const PSE_KEY = "pse" as const;
const PSE_ACTUAL_KEY = "pseActual" as const;
const RDN_KEY = "rdn" as const;
const CEN_P50_KEY = "cenP50" as const;

const formatForecast = (slot: ForecastSlot) => `${slot.p50.toFixed(2)} PLN/MWh`;

const PricesChartTooltip = ({
  active,
  payload,
  lockedIndex,
  allData,
  onPrev,
  onNext,
  pseLabel,
  pseActualLabel,
  rdnLabel,
  cenForecastLabel,
  pseColor,
  pseActualColor,
  rdnColor,
  forecastColor,
}: {
  active?: boolean;
  payload?: {
    dataKey: string;
    payload: Row;
    value: unknown;
    color: string;
  }[];
  lockedIndex: number | null;
  allData: Row[] | undefined;
  onPrev: () => void;
  onNext: () => void;
  pseLabel: string;
  pseActualLabel: string;
  rdnLabel: string;
  cenForecastLabel: string;
  pseColor: string;
  pseActualColor: string;
  rdnColor: string;
  forecastColor: string;
}) => {
  const theme = useTheme();
  const isLocked = lockedIndex !== null;

  if (!isLocked && (!active || !payload?.length)) return null;

  const row = isLocked ? allData?.[lockedIndex] : payload?.[0]?.payload;
  if (!row) return null;

  const canGoPrev = isLocked ? lockedIndex > 0 : true;
  const canGoNext = isLocked ? lockedIndex < (allData?.length ?? 1) - 1 : true;

  return (
    <Paper
      elevation={0}
      onClick={(e) => e.stopPropagation()}
      onMouseDown={(e) => e.stopPropagation()}
      onMouseMove={(e) => e.stopPropagation()}
      sx={{
        m: 1,
        border: "solid",
        borderWidth: 2,
        borderColor: "divider",
        backgroundColor:
          theme.palette.mode === "dark"
            ? "rgba(18, 18, 18, 0.85)"
            : "rgba(255, 255, 255, 0.95)",
        minWidth: 220,
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        gap={0.5}
        sx={{
          borderBottom: "solid",
          borderColor: "divider",
          borderWidth: "0 0 1px 0",
          px: 0.5,
          py: 0.5,
        }}
      >
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onPrev();
          }}
          disabled={!canGoPrev}
          sx={{ p: 0.25 }}
        >
          <Icon sx={{ fontSize: 18 }}>chevron_left</Icon>
        </IconButton>
        <Typography sx={{ flex: 1, textAlign: "center", fontWeight: 600 }}>
          {slotPeriod(row.time)}
        </Typography>
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            onNext();
          }}
          disabled={!canGoNext}
          sx={{ p: 0.25 }}
        >
          <Icon sx={{ fontSize: 18 }}>chevron_right</Icon>
        </IconButton>
      </Stack>
      <Stack gap={0.5} sx={{ p: 1.5 }}>
        {[
          {
            key: PSE_KEY,
            label: pseLabel,
            text: row.pse !== null ? `${row.pse.toFixed(2)} PLN/MWh` : "—",
            color: pseColor,
            dashed: false,
          },
          // Settled CEN exists only for past days; hide the row until it does.
          ...(row.pseActual !== null
            ? [
                {
                  key: PSE_ACTUAL_KEY,
                  label: pseActualLabel,
                  text: `${row.pseActual.toFixed(2)} PLN/MWh`,
                  color: pseActualColor,
                  dashed: false,
                },
              ]
            : []),
          ...(row.cenForecast
            ? [
                {
                  key: CEN_P50_KEY,
                  label: cenForecastLabel,
                  text: formatForecast(row.cenForecast),
                  color: forecastColor,
                  dashed: true,
                },
              ]
            : []),
          {
            key: RDN_KEY,
            label: rdnLabel,
            text: row.rdn !== null ? `${row.rdn.toFixed(2)} PLN/MWh` : "—",
            color: rdnColor,
            dashed: false,
          },
        ].map(({ key, label, text, color, dashed }) => (
          <Stack key={key} direction="row" alignItems="center" gap={1}>
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: 0.5,
                backgroundColor: dashed ? "transparent" : color,
                border: dashed ? `2px dashed ${color}` : undefined,
              }}
            />
            <Typography variant="body2" fontWeight="light" sx={{ flex: 1 }}>
              {label}
            </Typography>
            <Typography variant="body2">{text}</Typography>
          </Stack>
        ))}
      </Stack>
    </Paper>
  );
};

const YAxisStackedTick = ({
  x,
  y,
  payload,
  fill,
}: {
  x?: number;
  y?: number;
  payload?: { value: number };
  fill?: string;
}) => (
  <g transform={`translate(${x ?? 0},${y ?? 0})`}>
    <text x={-4} dy={-2} textAnchor="end" fontSize={11} fill={fill}>
      {payload?.value}
    </text>
    <text x={-4} dy={10} textAnchor="end" fontSize={9} fill={fill}>
      PLN
    </text>
  </g>
);

const PricesChart = ({
  data,
  forecast,
  showCurrentSlot,
}: {
  data: PricesData;
  forecast?: PriceForecastResponse;
  showCurrentSlot?: boolean;
}) => {
  const theme = useTheme();
  const formatMessage = useFormatMessage();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const currentSlot = getCurrentSlot(
    selectedPowerPlant?.timezone || "Europe/Warsaw",
  );

  const pseLabel = formatMessage("prices.pse");
  // The chart has room for the full name; the table header uses the short one.
  const pseActualLabel = formatMessage("prices.pseActualFull");
  const rdnLabel = formatMessage("prices.rdn");
  const cenForecastLabel = formatMessage("prices.cenForecast");
  // Two distinct hues for the imbalance price (see priceSeriesColors); red
  // stays reserved for "below zero".
  const { pse: pseColor, pseSettled: pseActualColor } = priceSeriesColors(
    theme.palette.mode,
  );
  const rdnColor = theme.palette.primary.main;
  // The prediction continues the PSE line in its colour, dotted.
  const forecastColor = pseColor;

  const rows: Row[] = useMemo(() => {
    const latest = new Map(
      (forecast?.cen?.slots ?? []).map((s) => [s.time, s]),
    );
    const history = new Map(
      (forecast?.cen?.history ?? []).map((s) => [s.time, s]),
    );
    // A forecast is frozen the moment its slot begins: from then on the
    // chart shows the forecast that stood at that moment, never a later
    // run's revision of a slot that is already under way or over. Only slots
    // still ahead take the newest run.
    const today = getCurrentDate(selectedPowerPlant?.timezone || "Europe/Warsaw");
    const started = (time: string) =>
      data.date < today || (data.date === today && time <= currentSlot);
    const built: Row[] = data.prices.map((p) => {
      const cenForecast = started(p.time)
        ? (history.get(p.time) ?? null)
        : (latest.get(p.time) ?? history.get(p.time) ?? null);
      return {
        time: p.time,
        pse: p.pse,
        pseActual: p.pseActual,
        rdn: p.rdn,
        cenForecast,
        cenP50: cenForecast?.p50 ?? null,
      };
    });
    // Start the dashed line on the last measured point so the two read as
    // one line: measured in red, then predicted in blue.
    const firstForecast = built.findIndex((r) => r.cenP50 !== null);
    if (firstForecast > 0 && built[firstForecast - 1].pse !== null) {
      built[firstForecast - 1].cenP50 = built[firstForecast - 1].pse;
    }
    return built;
  }, [data.prices, forecast, currentSlot, data.date, selectedPowerPlant?.timezone]);

  const hasData = rows.some(
    (r) =>
      r.pse !== null ||
      r.pseActual !== null ||
      r.rdn !== null ||
      r.cenForecast !== null,
  );
  const hasActuals = rows.some((r) => r.pseActual !== null);
  const hasCenForecast = rows.some((r) => r.cenForecast !== null);

  const { lockedIndex, chartEventHandlers, tooltipProps, navigationProps } =
    useChartNavigation({
      data: rows,
      dataKey: PSE_KEY,
      seriesType: "line",
      seriesColor: pseColor,
      theme,
      getX: (r) => r.time,
    });

  if (!hasData) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: 280,
        }}
      >
        <Typography color="text.secondary" variant="body2">
          {formatMessage("prices.noData")}
        </Typography>
      </Box>
    );
  }

  const lockedRow = lockedIndex !== null ? rows[lockedIndex] : null;

  return (
    <ResponsiveContainer width="100%" height={320}>
      <ComposedChart
        data={rows}
        margin={{ top: 8, right: 16, left: 0, bottom: 0 }}
        {...chartEventHandlers}
      >
        <CartesianGrid strokeDasharray="3 3" stroke={theme.palette.divider} />
        <XAxis
          dataKey="time"
          tick={{ fontSize: 11, fill: theme.palette.text.secondary }}
          interval={15}
        />
        <YAxis
          tick={<YAxisStackedTick fill={theme.palette.text.secondary} />}
          width={44}
          domain={["auto", "auto"]}
          padding={{ top: 20, bottom: 20 }}
        />
        <Tooltip
          position={{ y: 0 }}
          {...tooltipProps}
          content={
            <PricesChartTooltip
              {...navigationProps}
              pseLabel={pseLabel}
              pseActualLabel={pseActualLabel}
              rdnLabel={rdnLabel}
              cenForecastLabel={cenForecastLabel}
              pseColor={pseColor}
              pseActualColor={pseActualColor}
              rdnColor={rdnColor}
              forecastColor={forecastColor}
            />
          }
        />
        <Legend content={<WrappingLegend />} />
        <ReferenceLine
          y={0}
          stroke={theme.palette.error.main}
          strokeDasharray="4 4"
        />
        {showCurrentSlot && (
          <ReferenceLine
            x={currentSlot}
            stroke={theme.palette.primary.main}
            strokeDasharray="4 4"
            // At the bottom, where it doesn't sit on top of the lines.
            label={{
              value: formatMessage("prices.now"),
              fontSize: 10,
              fill: theme.palette.primary.main,
              position: "insideBottom",
              offset: 6,
            }}
          />
        )}
        {lockedRow && (
          <ReferenceLine
            x={lockedRow.time}
            stroke={theme.palette.divider}
            strokeDasharray="5 5"
            strokeWidth={2}
          />
        )}
        {lockedRow && lockedRow.pse !== null && (
          <ReferenceDot
            x={lockedRow.time}
            y={lockedRow.pse}
            r={4}
            isFront
            fill={pseColor}
            stroke="#fff"
            strokeWidth={2}
          />
        )}
        {lockedRow && lockedRow.pseActual !== null && (
          <ReferenceDot
            x={lockedRow.time}
            y={lockedRow.pseActual}
            r={4}
            isFront
            fill={pseActualColor}
            stroke="#fff"
            strokeWidth={2}
          />
        )}
        {lockedRow && lockedRow.cenP50 !== null && (
          <ReferenceDot
            x={lockedRow.time}
            y={lockedRow.cenP50}
            r={4}
            isFront
            fill={forecastColor}
            stroke="#fff"
            strokeWidth={2}
          />
        )}
        {lockedRow && lockedRow.rdn !== null && (
          <ReferenceDot
            x={lockedRow.time}
            y={lockedRow.rdn}
            r={4}
            isFront
            fill={rdnColor}
            stroke="#fff"
            strokeWidth={2}
          />
        )}
        <Line
          type="monotone"
          dataKey={PSE_KEY}
          legendType="plainline"
          name={pseLabel}
          stroke={pseColor}
          strokeWidth={2}
          dot={false}
          activeDot={lockedIndex !== null ? false : undefined}
          connectNulls={false}
          isAnimationActive={false}
        />
        {hasActuals && (
          <Line
            type="monotone"
            dataKey={PSE_ACTUAL_KEY}
            legendType="plainline"
            name={pseActualLabel}
            stroke={pseActualColor}
            strokeWidth={2}
            dot={false}
            activeDot={lockedIndex !== null ? false : undefined}
            connectNulls={false}
            isAnimationActive={false}
          />
        )}
        {hasCenForecast && (
          <Line
            type="monotone"
            dataKey={CEN_P50_KEY}
            legendType="plainline"
            name={cenForecastLabel}
            stroke={forecastColor}
            strokeWidth={2}
            strokeDasharray="4 4"
            dot={false}
            activeDot={lockedIndex !== null ? false : undefined}
            connectNulls={false}
            isAnimationActive={false}
          />
        )}
        <Line
          type="monotone"
          dataKey={RDN_KEY}
          legendType="plainline"
          name={rdnLabel}
          stroke={rdnColor}
          strokeWidth={2}
          dot={false}
          activeDot={lockedIndex !== null ? false : undefined}
          connectNulls={false}
          isAnimationActive={false}
        />
      </ComposedChart>
    </ResponsiveContainer>
  );
};

const PricesTable = ({
  data,
  forecast,
}: {
  data: PricesData;
  forecast?: PriceForecastResponse;
}) => {
  const formatMessage = useFormatMessage();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const timezone = selectedPowerPlant?.timezone || "Europe/Warsaw";
  const currentSlot = getCurrentSlot(timezone);
  const isCurrentDay = data.date === getCurrentDate(timezone);
  const containerRef = useRef<HTMLDivElement>(null);
  const currentRowRef = useRef<HTMLTableRowElement>(null);
  const hasActuals = data.prices.some((p) => p.pseActual !== null);
  // Slots already under way or over keep the forecast that stood when they
  // began; only slots still ahead show the newest run.
  const today = getCurrentDate(timezone);
  const started = (time: string) =>
    data.date < today || (data.date === today && time <= currentSlot);
  const history = new Map((forecast?.cen?.history ?? []).map((s) => [s.time, s]));
  const cenForecasts = new Map(
    [
      ...(forecast?.cen?.history ?? []),
      ...(forecast?.cen?.slots ?? []).filter((s) => !started(s.time) || !history.has(s.time)),
    ].map((s) => [s.time, s]),
  );

  // The live slot sits somewhere in the middle of 96 rows, so centre it in the
  // scroll box instead of opening on 00:00 and making the user hunt for now.
  // Only the container scrolls — moving scrollTop by the measured delta leaves
  // the page itself where it is.
  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (!isCurrentDay) {
      container.scrollTop = 0;
      return;
    }
    const row = currentRowRef.current;
    if (!row) return;
    const containerBox = container.getBoundingClientRect();
    const rowBox = row.getBoundingClientRect();
    container.scrollTop +=
      rowBox.top - containerBox.top - (containerBox.height - rowBox.height) / 2;
  }, [currentSlot, isCurrentDay, data.date]);

  return (
    <StickyTable ref={containerRef} maxHeight={400}>
        <TableHead>
          {/* Headers and the period never wrap; the container scrolls instead. */}
          <TableRow sx={{ "& th": { whiteSpace: "nowrap" } }}>
            <TableCell>{formatMessage("prices.tableTime")}</TableCell>
            <TableCell align="right">{formatMessage("prices.pse")}</TableCell>
            {hasActuals && (
              <TableCell align="right">
                {formatMessage("prices.pseActual")}
              </TableCell>
            )}
            <TableCell align="right">{formatMessage("prices.rdn")}</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {data.prices.map((p) => {
            const isCurrent = p.time === currentSlot;
            return (
              <TableRow
                key={p.time}
                ref={isCurrent ? currentRowRef : undefined}
                sx={
                  isCurrent
                    ? {
                        backgroundColor: "action.hover",
                        "&& td:first-of-type": { backgroundColor: "action.hover" },
                      }
                    : undefined
                }
              >
                <TableCell sx={{ whiteSpace: "nowrap" }}>
                  {slotPeriod(p.time)}
                </TableCell>
                {/* The measured preliminary when PSE has published it; until
                    then the nowcast, pulsing in blue so it reads as a guess. */}
                {p.pse !== null ? (
                  <TableCell
                    align="right"
                    sx={
                      p.pse < 0
                        ? { color: "error.main", fontWeight: 600 }
                        : undefined
                    }
                  >
                    {p.pse.toFixed(2)}
                  </TableCell>
                ) : (
                  <TableCell align="right">
                    {cenForecasts.has(p.time) ? (
                      // Only the number pulses: on the cell the animation
                      // would take the row divider (and the current-slot
                      // highlight) along with it.
                      <Box
                        component="span"
                        sx={{
                          ...pulseSx(),
                          // Same colours as measured values; the pulse
                          // alone marks it as predicted.
                          color:
                            cenForecasts.get(p.time)!.p50 < 0
                              ? "error.main"
                              : "text.primary",
                          fontWeight: 600,
                        }}
                      >
                        {cenForecasts.get(p.time)!.p50.toFixed(2)}
                      </Box>
                    ) : (
                      "—"
                    )}
                  </TableCell>
                )}
                {hasActuals && (
                  <TableCell
                    align="right"
                    sx={
                      p.pseActual !== null && p.pseActual < 0
                        ? { color: "error.main", fontWeight: 600 }
                        : undefined
                    }
                  >
                    {p.pseActual !== null ? p.pseActual.toFixed(2) : "—"}
                  </TableCell>
                )}
                <TableCell
                  align="right"
                  sx={
                    p.rdn !== null && p.rdn < 0
                      ? { color: "error.main", fontWeight: 600 }
                      : undefined
                  }
                >
                  {p.rdn !== null ? p.rdn.toFixed(2) : "—"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
    </StickyTable>
  );
};

export const PricesPage = () => {
  const formatMessage = useFormatMessage();
  const { dateFnsLocale } = useLocaleContext();
  const [selectedDate, setSelectedDate] = useState(() => new Date());
  const [datePickerOpen, setDatePickerOpen] = useState(false);
  const { data, isLoading, isFetching, dataUpdatedAt } =
    useGetPrices(selectedDate);
  const { refresh, isRefreshing } = useRefreshPrices(selectedDate);
  const { data: forecast } = useGetPriceForecast(selectedDate);
  // Reveals each source's age under its price once a refresh the user asked
  // for has landed.
  const { flashId, flash } = useFlashOnce(sourceFlashDurationMs);
  const { data: availability } = useGetPricesAvailability();
  const selectedPowerPlant = useAppSelector(getSelectedPowerPlant);
  const timezone = selectedPowerPlant?.timezone || "Europe/Warsaw";
  // Keeps the ages at the bottom counting up while the page sits open.
  const now = useNow();

  const dayData = data?.today;
  const dateLabel = format(selectedDate, "EEEE, d MMMM yyyy");
  const { minDate, maxDate } = useMemo(() => {
    const today = new Date();
    const fallbackMin = subYears(today, 1);
    const apiMin = availability?.minDate
      ? new Date(`${availability.minDate}T00:00:00`)
      : null;
    return {
      minDate: apiMin ?? fallbackMin,
      maxDate: addDays(today, 1),
    };
  }, [availability?.minDate]);
  const canGoNext = addDays(selectedDate, 1) <= maxDate;
  const canGoPrev = addDays(selectedDate, -1) >= minDate;

  const handleRefresh = async () => {
    await refresh();
    flash();
  };

  const stamp = (at: string | number) =>
    formatTimestamp(at, now, timezone, dateFnsLocale);

  const isBusy = isFetching || isRefreshing;
  const lastRefreshedLabel =
    data && dataUpdatedAt ? stamp(dataUpdatedAt) : null;
  // Per-source, because the two feeds move on completely different clocks:
  // PSE re-forecasts through the day, RDN lands once when the auction clears.
  const sourceAges = dayData
    ? [
        { label: formatMessage("prices.pse"), at: dayData.sources.pse },
        {
          label: formatMessage("prices.pseActual"),
          at: dayData.sources.pseActual,
        },
        { label: formatMessage("prices.rdn"), at: dayData.sources.rdn },
      ].flatMap(({ label, at }) => (at ? [`${label} ${stamp(at)}`] : []))
    : [];
  const sourceDataLabel = sourceAges.length ? sourceAges.join(" · ") : null;
  const forecastIssuedLabel = forecast?.cen
    ? `${formatMessage("prices.pse")} ${stamp(forecast.cen.issuedAt)}`
    : null;

  return (
    <PageLayout
      pageTitle={formatMessage("pageTitle.prices")}
      actions={
        <IconButton
          aria-label={formatMessage("prices.refresh")}
          onClick={() => void handleRefresh()}
          disabled={isBusy}
        >
          {/* Matched to the 24px icon so swapping them can't nudge the row. */}
          {isBusy ? <CircularProgress size={24} /> : <Icon>refresh</Icon>}
        </IconButton>
      }
    >
      <Stack gap={3}>
        <CurrentPricesWidget disableLink flashId={flashId} />

        <Card variant="outlined">
          <CardContent>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              gap={1}
              mb={2}
            >
              <IconButton
                size="small"
                aria-label={formatMessage("prices.previousDay")}
                onClick={() => setSelectedDate((d) => addDays(d, -1))}
                disabled={!canGoPrev}
              >
                <Icon>chevron_left</Icon>
              </IconButton>
              <Stack
                direction="row"
                alignItems="center"
                gap={1.25}
                sx={{
                  minWidth: 0,
                  flex: 1,
                  justifyContent: "center",
                  cursor: "pointer",
                  borderRadius: 1,
                  py: 0.5,
                  "&:hover": { backgroundColor: "action.hover" },
                }}
                onClick={() => setDatePickerOpen(true)}
                role="button"
                aria-label={formatMessage("prices.pickDate")}
              >
                <Typography
                  variant="subtitle1"
                  fontWeight={600}
                  textAlign="center"
                  noWrap
                >
                  {dateLabel}
                </Typography>
                <Icon
                  fontSize="small"
                  baseClassName="material-icons-outlined"
                  sx={{ color: "text.disabled" }}
                >
                  edit_calendar
                </Icon>
              </Stack>
              <IconButton
                size="small"
                aria-label={formatMessage("prices.nextDay")}
                onClick={() => setSelectedDate((d) => addDays(d, 1))}
                disabled={!canGoNext}
              >
                <Icon>chevron_right</Icon>
              </IconButton>
              <MobileDatePicker
                value={selectedDate}
                onChange={(d) => d && setSelectedDate(d)}
                open={datePickerOpen}
                onClose={() => setDatePickerOpen(false)}
                minDate={minDate}
                maxDate={maxDate}
                slotProps={{
                  textField: { sx: { display: "none" } },
                }}
              />
            </Stack>

            {isLoading ? (
              <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
                <CircularProgress />
              </Box>
            ) : dayData ? (
              <Stack gap={2}>
                <PricesChart
                  data={dayData}
                  forecast={forecast}
                  showCurrentSlot={isToday(selectedDate)}
                />
                <PricesTable data={dayData} forecast={forecast} />
              </Stack>
            ) : null}
          </CardContent>
        </Card>

        {(lastRefreshedLabel || sourceDataLabel || forecastIssuedLabel) && (
          <Stack sx={{ pl: 0.5 }}>
            {lastRefreshedLabel && (
              <Typography variant="caption" color="text.secondary">
                {formatMessage("prices.lastRefreshed")}: {lastRefreshedLabel}
              </Typography>
            )}
            {sourceDataLabel && (
              <Typography variant="caption" color="text.secondary">
                {formatMessage("prices.sourceData")}: {sourceDataLabel}
              </Typography>
            )}
            {forecastIssuedLabel && (
              <Typography variant="caption" color="text.secondary">
                {formatMessage("prices.forecastIssued")}: {forecastIssuedLabel}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>
    </PageLayout>
  );
};
